"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { FeedEntry } from "@theta/api-client";

/**
 * THE HOME FEED ORDER IS FROZEN PER VISIT — polls refresh card CONTENT in
 * place, but never move, insert or reorder cards under the reader (OSO-622).
 *
 * The motivating bug is the self-bump yank: `sort=newest` ranks a thread the
 * viewer has participated in by `bumpedAt` (backend/src/data/content.ts, the
 * participant half of the feed query), so writing a comment — or anyone else
 * commenting on that thread — teleports the card to the top on the next 10 s
 * poll, mid-read. The server's ordering is CORRECT (it is the right order for
 * the next visit); what is wrong is applying a new order to a screen someone
 * is currently reading. That is why the freeze is CLIENT-side: the cursor /
 * `?ifChanged=` machinery in `useHomeFeed.ts` is order-agnostic and stays
 * byte-for-byte untouched — the only place the yank ever happens is the
 * `pages.flatMap` in FeedClient, so this hook sits exactly between that
 * flatten and the render.
 *
 * The rules, and why each is what it is:
 *
 *  - SNAPSHOT WAITS FOR THE FIRST POST-MOUNT FETCH. On a remount within
 *    react-query's gcTime (5 min) the cached pages render instantly and a
 *    background refetch follows (`staleTime: 0`). Freezing the cached order
 *    would pin a stale sort and immediately raise a phantom pill — but a
 *    revisit is precisely when the reader is SUPPOSED to get the fresh order.
 *    So the hook renders `live` as-is until `fetchedAfterMount` — react-query's
 *    `isFetchedAfterMount`, false while only cached data has rendered — flips
 *    true, then freezes what that first fresh fetch delivered. A BOOLEAN, not
 *    `dataUpdatedAt`, deliberately: the stamp bumps on every poll completion
 *    and subscribing FeedClient to it re-renders every card per tick — the
 *    exact per-poll churn the freeze design forbids (plan-risks §2) — while
 *    the boolean flips exactly once per mount.
 *  - IN-PLACE UPDATES: an id present in both snapshot and live renders the
 *    LIVE entry — reactions, previews and counts stay current, the card just
 *    does not move. The same `entry.post` object references pass through, so
 *    downstream seed memos (useComments) keep holding across unchanged ticks.
 *  - GENUINELY NEW vs BUMPED-IN OLD (arbitration A6): a live id missing from
 *    the snapshot is classified by the post's TRUE creation instant,
 *    `post.createdAt` — never `sortedAt`, which is `bumpedAt` for participant
 *    threads and would misfile bumped old posts as new. Created after the
 *    freeze instant → held behind the "nye opslag" pill. Created before →
 *    an old post bumped into page 1: neither rendered (it would be a mid-read
 *    insertion) nor pilled (nothing on it is new to read) — it simply appears
 *    on the next visit. The freeze instant mixes the server's `sortedAt`
 *    ceiling with the client clock at snapshot time; a skewed client clock can
 *    only push a borderline post from "pill" to "next visit", never invent one.
 *  - DISPLACED ≠ DELETED: a snapshot id absent from live while new ids pushed
 *    into page 1 was displaced past the page window — dropping it mid-read is
 *    the yank in another costume, so it keeps rendering from the last-known
 *    cache. Absent with NOTHING new arrived = deleted/moderator-hidden → it
 *    leaves the screen on that tick (the §1.10 hide case). The one ambiguous
 *    tick — a hide and a new arrival together — keeps the hidden card until
 *    reveal/refresh; accepted, because the alternative reintroduces the yank
 *    for every ordinary displacement.
 *  - PAGINATION IS NOT "NEW": live ids past the LAST snapshot member are an
 *    older page the reader explicitly fetched — appended to the tail, never
 *    pilled. Duplicate ids across page boundaries (a post crossing a cursor
 *    between refetches) dedupe first-occurrence-wins, so React keys stay
 *    unique.
 *  - NO OWN-POST BYPASS: `PostComposer` is never mounted on /feed (composing
 *    happens on group pages), and coming back to /feed remounts this hook →
 *    a fresh snapshot with the new post on top. Do not add a "my post skips
 *    the pill" splice; there is no path that needs it.
 *
 * Per-visit flows this encodes: poll tick → content in place, new ids →
 * pill; pagination → appends through the untouched cursor chain; tab refocus
 * (refetchOnWindowFocus) → same visit, freeze HOLDS; route change/reload →
 * remount → fresh order. The pill click IS the in-page refresh; if a
 * pull-to-refresh ever lands it should call `reveal()`. If a sort switcher
 * lands, the snapshot must reset on sort change (pass sort as a reset key
 * then — not built speculatively).
 */

type PostId = FeedEntry["post"]["id"];

interface Snapshot {
  ids: PostId[];
  /** Epoch ms the order was frozen at — the classifier for "genuinely new". */
  frozenAt: number;
}

export interface FrozenFeed {
  /** What to render, in frozen order (live content, frozen positions). */
  entries: FeedEntry[];
  /** Genuinely-new posts held behind the pill. */
  pendingCount: number;
  /** Adopt the live order, clear the pill, scroll to top. */
  reveal: () => void;
}

/** Dedupe the flattened pages by post id, first occurrence wins (A6). */
function dedupeById(live: FeedEntry[]): FeedEntry[] {
  const seen = new Set<PostId>();
  const out: FeedEntry[] = [];
  for (const entry of live) {
    if (!seen.has(entry.post.id)) {
      seen.add(entry.post.id);
      out.push(entry);
    }
  }
  return out;
}

/**
 * The freeze instant: the newest `sortedAt` the snapshot carries, floored by
 * the client clock. A post created AFTER this cannot be in the snapshot, so
 * `createdAt > frozenAt` is a safe "genuinely new" test on server-issued
 * timestamps; `Date.now()` (within milliseconds of the fetch completion this
 * runs on the heels of) only widens the ceiling — see the skew note in the
 * header.
 */
function freezeInstantOf(entries: FeedEntry[], now: number): number {
  let max = now;
  for (const entry of entries) {
    const at = Date.parse(entry.sortedAt);
    if (Number.isFinite(at) && at > max) max = at;
  }
  return max;
}

export function useFrozenFeedOrder(
  live: FeedEntry[],
  fetchedAfterMount: boolean
): FrozenFeed {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  // Last-known entry per id, so a displaced card keeps rendering after it
  // falls out of the live window. A stable Map in state — READ during render,
  // MUTATED only in the sync effect below (the render rules forbid touching a
  // ref mid-render, and its contents never need to trigger a render of their
  // own: an entry read from here can only be one commit old, stored while the
  // post was still live, which is exactly the "last known" the rule wants).
  const [latest] = useState(() => new Map<PostId, FeedEntry>());

  const deduped = dedupeById(live);

  // The render-current live order, for `reveal` (an event handler) and the
  // freeze effects — written in an effect, never read during render.
  const liveRef = useRef<Snapshot>({ ids: [], frozenAt: 0 });

  // Declared BEFORE the freeze effects on purpose: effects run in order, so
  // liveRef and the last-known cache are current by the time they freeze.
  useEffect(() => {
    for (const entry of deduped) latest.set(entry.post.id, entry);
    liveRef.current = {
      ids: deduped.map((entry) => entry.post.id),
      frozenAt: freezeInstantOf(deduped, Date.now()),
    };
  });

  // Freeze on the first fetch that COMPLETED after mount — while
  // `fetchedAfterMount` is false only cached data from a previous visit has
  // rendered, and it passes through unfrozen (see the header: pinning the
  // cached order would raise a phantom pill).
  useEffect(() => {
    if (snapshot === null && fetchedAfterMount) {
      setSnapshot(liveRef.current);
    }
  }, [snapshot, fetchedAfterMount]);

  // Empty-feed escape: a pill hovering over a blank screen helps nobody, so a
  // feed frozen at [] adopts the first posts that arrive instead.
  const liveCount = live.length;
  useEffect(() => {
    if (snapshot !== null && snapshot.ids.length === 0 && liveCount > 0) {
      setSnapshot(liveRef.current);
    }
  }, [snapshot, liveCount]);

  const reveal = useCallback(() => {
    // Re-freezing also re-records the freeze instant, so posts arriving after
    // a reveal classify against the reveal, not the original page load.
    setSnapshot(liveRef.current);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Not frozen yet: hand the live order straight through.
  if (snapshot === null) {
    return { entries: deduped, pendingCount: 0, reveal };
  }

  // Pure per-render derivation — the snapshot is the only React state.
  const liveIndex = new Map<PostId, number>();
  deduped.forEach((entry, index) => liveIndex.set(entry.post.id, index));
  const orderSet = new Set(snapshot.ids);

  // The last live position still occupied by a snapshot member: anything
  // beyond it is pagination the reader asked for, not arrival.
  let lastKnownIdx = -1;
  liveIndex.forEach((index, id) => {
    if (orderSet.has(id) && index > lastKnownIdx) lastKnownIdx = index;
  });

  const trailing: FeedEntry[] = [];
  let pendingCount = 0;
  let displacing = 0;
  for (const [id, index] of liveIndex) {
    if (orderSet.has(id)) continue;
    // Trailing (pagination) only exists relative to a snapshot member still
    // live: with zero overlap (`lastKnownIdx` still -1 — a refocus after 20+
    // arrivals pushed the whole snapshot past page 1) every live id would
    // otherwise classify as trailing and every frozen card as deleted — the
    // silent full reorder the freeze exists to prevent. Pagination alone
    // cannot produce a disjoint live list (page 1 leads the flattened array),
    // so zero overlap is arrival displacement and takes the branch below.
    if (lastKnownIdx >= 0 && index > lastKnownIdx) {
      trailing.push(deduped[index]);
      continue;
    }
    // A non-trailing new id displaced a snapshot member past the page window
    // whether it is pill-worthy or a bumped-in old post — both count for the
    // displaced-vs-deleted rule below.
    displacing += 1;
    if (Date.parse(deduped[index].post.createdAt) > snapshot.frozenAt) {
      pendingCount += 1;
    }
    // else: bumped-in old — neither rendered nor pilled (A6), next visit.
  }

  const entries: FeedEntry[] = [];
  for (const id of snapshot.ids) {
    const index = liveIndex.get(id);
    if (index !== undefined) {
      // Present in live: render the LIVE entry, frozen position.
      entries.push(deduped[index]);
    } else if (displacing > 0) {
      // Displaced past the window by arrivals: keep it, from last-known.
      const cached = latest.get(id);
      if (cached) entries.push(cached);
    }
    // else: gone with nothing new arrived = deleted/hidden → drops out.
  }
  entries.push(...trailing);

  return { entries, pendingCount, reveal };
}
